import React, { useEffect, useState } from "react";
import { XIcon, DeviceMobileIcon, DownloadIcon } from "@phosphor-icons/react";

const DISMISS_KEY = "bidorah_install_prompt_dismissed_until";
const RE_PROMPT_DAYS = 14;

export default function InstallAppPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show if already installed (PWA / standalone mode)
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Check dismissal cooldown
    const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedUntil && dismissedUntil > Date.now()) return;

    const ua = window.navigator.userAgent || "";
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(isIOSDevice);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setVisible(true), 4000); // wait 4s so user can see the site first
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari doesn't fire beforeinstallprompt — show custom help banner instead
    if (isIOSDevice) {
      setTimeout(() => setVisible(true), 6000);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  };

  const dismiss = () => {
    const until = Date.now() + RE_PROMPT_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto bg-zinc-900 text-white rounded-2xl shadow-2xl p-4 flex items-start gap-3 border border-zinc-800 animate-in slide-in-from-bottom-6"
      data-testid="install-app-prompt"
    >
      <div className="h-10 w-10 rounded-xl bg-[#1D78AB] grid place-items-center shrink-0">
        <DeviceMobileIcon size={22} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm">Install Bidorah</div>
        {isIOS ? (
          <p className="text-xs text-zinc-300 leading-relaxed mt-1">
            Tap the <b>Share</b> icon in Safari, then <b>Add to Home Screen</b>.
            Launches full-screen like a native app.
          </p>
        ) : (
          <p className="text-xs text-zinc-300 leading-relaxed mt-1">
            Get the full-screen Bidorah experience. One tap.
          </p>
        )}
        <div className="flex gap-2 mt-3">
          {deferredPrompt && !isIOS && (
            <button
              onClick={install}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1D78AB] hover:bg-[#165d85] text-white text-xs font-bold"
              data-testid="install-app-confirm"
            >
              <DownloadIcon size={13} weight="bold" /> Install
            </button>
          )}
          <button
            onClick={dismiss}
            className="px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-300"
            data-testid="install-app-dismiss"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="text-zinc-500 hover:text-zinc-300 p-1"
        aria-label="Dismiss"
      >
        <XIcon size={16} />
      </button>
    </div>
  );
}
